import { getAuthToken } from "@skilitics/designer-auth/state";
import type { HttpApiCreator, HttpRequestOptions } from "@skilitics/nautilus-core-designer";

export const createApiCreator = (baseUrl: string): HttpApiCreator => {
  const getUri = (path: string): string => {
    if (baseUrl.endsWith("/") && path.startsWith("/")) {
      return `${baseUrl}${path.slice(1)}`;
    }
    return `${baseUrl}/${path}`;
  };

  const emptyRequest = async (method: string, path: string, options?: HttpRequestOptions): Promise<Response> => {
    return await fetch(getUri(path), {
      method,
      headers: {
        "x-skilitics-token": await getAuthToken(),
      },
      signal: options?.signal,
    });
  };

  const jsonRequest = async (
    method: string,
    path: string,
    payload: unknown,
    options?: HttpRequestOptions,
  ): Promise<Response> => {
    return await fetch(getUri(path), {
      method,
      headers: {
        "x-skilitics-token": await getAuthToken(),
        "content-type": "application/json",
      },
      body: JSON.stringify(payload),
      signal: options?.signal,
    });
  };

  type NoPayloadRequest<TRes> = (options?: HttpRequestOptions) => Promise<TRes>;
  type WithPayloadRequest<TReq, TRes> = (payload: TReq, options?: HttpRequestOptions) => Promise<TRes>;

  // todo: better error handling, void responses
  const handleEmptyResponse = async (res: Response): Promise<void> => {
    if (!res.ok) {
      throw new Error(`Error: ${res.status} ${res.statusText}`);
    }
  };
  const handleJsonResponse = async <TRes>(res: Response): Promise<TRes> => {
    if (!res.ok) {
      throw new Error(`Error: ${res.status} ${res.statusText}`);
    }
    return (await res.json()) as Promise<TRes>;
  };

  return {
    getUri,
    getCreate: <TRes>(path: string): NoPayloadRequest<TRes> => {
      return async (options?: HttpRequestOptions): Promise<TRes> => {
        const res = await emptyRequest("GET", path, options);
        return await handleJsonResponse<TRes>(res);
      };
    },
    postCreate: <TReq, TRes = void>(path: string): WithPayloadRequest<TReq, TRes> => {
      return async (payload: TReq, options?: HttpRequestOptions): Promise<TRes> => {
        const res = await jsonRequest("POST", path, payload, options);
        return await handleJsonResponse<TRes>(res);
      };
    },
    putCreate: <TReq>(path: string): WithPayloadRequest<TReq, void> => {
      return async (payload: TReq, options?: HttpRequestOptions): Promise<void> => {
        const res = await jsonRequest("PUT", path, payload, options);
        return await handleEmptyResponse(res);
      };
    },
    patchCreate: <TReq, TRes = void>(path: string): WithPayloadRequest<TReq, TRes> => {
      return async (payload: TReq, options?: HttpRequestOptions): Promise<TRes> => {
        const res = await jsonRequest("PATCH", path, payload, options);
        return await handleJsonResponse<TRes>(res);
      };
    },
    deleteCreate: (path: string): NoPayloadRequest<void> => {
      return async (options?: HttpRequestOptions): Promise<void> => {
        const res = await emptyRequest("DELETE", path, options);
        return await handleEmptyResponse(res);
      };
    },
  };
};
